import { DatePipe, DecimalPipe, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SocialListeningPlatformIconComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/mention-cards-section/social-listening-platform-icon.component';
import { SocialListeningPlatformType } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/platform-chips/platform-chip.component';
import {
	normalizeSocialListeningPlatform,
	SOCIAL_LISTENING_MENTION_ORIGIN_MARKS
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { formatPlatformLabel } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/analytics-label.utils';
import {
	DialogService,
	IconComponent,
	ShortNumberPipe,
	TypographyComponent
} from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportDateRange } from '../../../../models/preview/preview.models';
import {
	ReportBlockData,
	ReportBlockQuote
} from '../../../../services/report-blocks.service';
import { ReportQuoteChange } from '../../../../services/report-quote';
import { SlideNoDataComponent } from '../../slides/slide-no-data/slide-no-data.component';
import {
	ReportQuoteSwapDialogComponent,
	ReportQuoteSwapDialogData
} from './report-quote-swap-dialog.component';

/** Platforms whose "author" is a publication, which has no engagement. */
const WEB_SOURCES: ReadonlySet<string> = new Set([
	'news',
	'blogs',
	'forums',
	'web'
]);

const SENTIMENT_LABELS: Record<ReportBlockQuote['sentiment'], string> = {
	positive: 'Positive',
	neutral: 'Neutral',
	negative: 'Negative'
};

const GONE_NOTE = 'This mention is not available.';

/**
 * A quote card: one mention in the report, in its author's own words.
 *
 * Drawn from what the backend resolved and nothing else — the card fetches
 * nothing, so it prints the same in the editor, in the PDF (whose page stops
 * checking for changes after its first pass) and on the shared page. The words
 * come first and are clamped rather than cut, then who said it, where and
 * when, and the post's engagement as the mention card in the feed shows it.
 *
 * A hidden author is left out by the SERVER, not here: the name, handle,
 * avatar and link simply do not arrive (see `ReportBlockQuote`). What this card
 * does is say plainly that the author is hidden, so the reader does not take
 * a card with no name for a broken one.
 *
 * EDITOR ONLY, over the card: Hide author and Swap mention. They show on hover
 * or focus through the same `--report-chart-types-*` properties the chart-type
 * menu uses, so every block on the page reveals its controls the same way.
 * Each change goes to the page, which re-reads the block and saves it by
 * itself; the controls wait for the read to come back before taking another.
 *
 * ONE CARD OF A LIST is given its mention as `item` instead of a block. It is
 * drawn compact, to share a slide with nine others, and its only control is
 * Keep this one: the list hides its authors with one switch of its own, and a
 * post the list read cannot be swapped for another.
 */
@Component({
	standalone: true,
	selector: 'app-report-quote-card',
	templateUrl: './report-quote-card.component.html',
	styleUrls: ['./report-quote-card.component.scss'],
	imports: [
		NgIf,
		DatePipe,
		DecimalPipe,
		MatTooltipModule,
		IconComponent,
		ShortNumberPipe,
		SocialListeningPlatformIconComponent,
		SlideNoDataComponent,
		TypographyComponent
	]
})
export class ReportQuoteCardComponent implements OnDestroy {
	@Input() set block(value: ReportBlockData) {
		this._block = value;
		this.goneNote = value?.note || GONE_NOTE;
		this.show(value?.quote ?? null);
	}
	get block(): ReportBlockData {
		return this._block;
	}

	/** One card of a list of mentions: its mention, drawn compact. */
	@Input() set item(value: ReportBlockQuote | null) {
		this.isItem = true;
		this.show(value);
	}

	/** Whose mentions Swap mention lists, and for which window. */
	@Input() brandId = '';
	@Input() dateRange: ReportDateRange | null = null;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	/**
	 * The page is reading its blocks again. The controls wait for it: the
	 * card they would change is the one about to be replaced, and a second
	 * change sent over the first could come back first and be overwritten.
	 */
	@Input() busy = false;

	/** The reader swapped the mention or hid or showed its author. */
	@Output() quoteChange = new EventEmitter<ReportQuoteChange>();
	/** A list's card only: the reader wants this post as a quote card of its own. */
	@Output() keep = new EventEmitter<void>();

	quote: ReportBlockQuote | null = null;
	/** Drawn as one card of a list (`item`), not as a quote card of its own. */
	isItem = false;
	/** Why there is no quote: the block's note, which the server writes. */
	goneNote = GONE_NOTE;
	platform: SocialListeningPlatformType = 'web';
	platformLabel = '';
	isWebSource = false;
	/** `@handle` for a social author; a site's own name is left as it is. */
	handle = '';
	initials = '';
	sentimentLabel = '';
	/** "Own post" or "Comment on your post"; empty for a mention found by search. */
	originMark = '';
	parentPostUrl = '';

	private _block!: ReportBlockData;
	private destroy$ = new Subject<void>();

	constructor(private dialogService: DialogService) {}

	toggleAuthor(): void {
		if (!this.quote || this.busy) {
			return;
		}
		this.quoteChange.emit({ hide_author: !this.quote.author_hidden });
	}

	keepThis(): void {
		if (!this.quote || this.busy) {
			return;
		}
		this.keep.emit();
	}

	/**
	 * Pick another mention for this card, from the brand's mentions in the
	 * report's period. The card keeps its heading and whether its author is
	 * hidden: those are the reader's choices about the card, not the post.
	 */
	swap(): void {
		if (this.busy || !this.brandId) {
			return;
		}
		this.dialogService
			.open<
				ReportQuoteSwapDialogComponent,
				ReportQuoteSwapDialogData,
				string
			>(
				ReportQuoteSwapDialogComponent,
				{
					brandId: this.brandId,
					range: this.dateRange,
					currentMentionId: this.quote?.mention_id ?? null
				},
				{ ariaLabel: 'Swap mention' }
			)
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(mentionId => {
				if (mentionId && mentionId !== this.quote?.mention_id) {
					this.quoteChange.emit({ mention_id: mentionId });
				}
			});
	}

	/**
	 * An avatar that will not load leaves the initials under it showing.
	 * Done on the element rather than through a flag, because the PDF's page
	 * no longer checks for changes by the time a late image fails.
	 */
	hideBrokenImage(event: Event): void {
		(event.target as HTMLElement).style.display = 'none';
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private show(quote: ReportBlockQuote | null): void {
		this.quote = quote;
		const platform = quote?.platform ?? '';
		this.platform = normalizeSocialListeningPlatform(platform);
		this.platformLabel = formatPlatformLabel(platform);
		this.isWebSource = WEB_SOURCES.has(platform);
		this.handle = this.buildHandle(quote);
		this.initials = this.buildInitials(quote);
		this.sentimentLabel = quote
			? SENTIMENT_LABELS[quote.sentiment] ?? ''
			: '';
		this.originMark = quote?.origin
			? SOCIAL_LISTENING_MENTION_ORIGIN_MARKS[quote.origin] ?? ''
			: '';
		this.parentPostUrl =
			quote?.origin === 'comment_on_own_post'
				? quote.parent_url ?? ''
				: '';
	}

	private buildHandle(quote: ReportBlockQuote | null): string {
		const handle = (quote?.author_handle ?? '').trim();
		if (!handle || !quote) {
			return '';
		}
		if (WEB_SOURCES.has(quote.platform)) {
			return handle === quote.author_name ? '' : handle;
		}
		const bare = handle.replace(/^@/, '');
		return bare === quote.author_name ? '' : `@${bare}`;
	}

	private buildInitials(quote: ReportBlockQuote | null): string {
		const name = (quote?.author_name ?? '').replace(/^@/, '').trim();
		const words = name.split(/\s+/).filter(Boolean);
		return words
			.slice(0, 2)
			.map(word => word.charAt(0).toUpperCase())
			.join('');
	}
}
